import React, { useState, useEffect } from "react";
import { Global } from "@emotion/react";
import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { grey } from "@mui/material/colors";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useAppContext } from "../../AppContext";
import axios from "axios";
import { MyDomain } from "../../App";
import { useParams } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { useTranslation } from "react-i18next";

const Root = styled("div")(({ theme }) => ({
  height: "100%",
  backgroundColor: grey[100],
}));

const StyledBox = styled("div")(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderTopLeftRadius: "24px",
  borderTopRightRadius: "24px",
}));

const Puller = styled("div")(({ theme }) => ({
  width: 36,
  height: 4,
  backgroundColor: "#cbd5e1",
  borderRadius: 2,
  position: "absolute",
  top: 12,
  left: "calc(50% - 18px)",
}));

function MPHomeModalMakeApp(props) {
  const { t } = useTranslation();
  const { BarberPhoneNumber } = useParams();

  const {
    OpenDrawer,
    setOpenDrawer,
    ObjectData,
    setSelectedServices,
    SelectedServices,
    User,
    AllData,
    ChoosedApps,
    triggerRefresh,
    setValue,
    setStep,
    step,
    Lang,
  } = useAppContext();

  const getCurrencySymbol = (countryCode) => {
    const currencyMap = {
      "+972": "₪",
      "+46": "kr",
      "+1": "$",
      "+44": "£",
      "+49": "€",
    };
    return currencyMap[countryCode] || "₪";
  };

  const currencySymbol = getCurrencySymbol(User?.countryNumber);
  const isRTL = Lang === "ar" || Lang === "he";
  const { window } = props;
  const [IsLoading, setIsLoading] = useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpenDrawer(newOpen);
  };

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const totalDuration = SelectedServices.reduce(
    (sum, service) => sum + (service.Duration || 0),
    0,
  );

  const calculateActualEndTime = () => {
    if (!ObjectData?.timeStart || totalDuration === 0) {
      return ObjectData?.timeEnd || "";
    }

    const [hours, minutes] = ObjectData.timeStart.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + totalDuration;
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;

    return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
  };

  const actualEndTime = calculateActualEndTime();

  const BookApp = async () => {
    if (!User || !User.name || !User.phoneNumber) {
      console.error("User data not available:", User);
      alert("Please login first to book an appointment.");
      setOpenDrawer(false);
      return;
    }

    setIsLoading(true);

    const findAdditionalAppointments = () => {
      const additionalAppIds = [];
      let accumulatedTime = ObjectData.timeLength;
      let currentIndex = -1;

      for (let i = 0; i < ChoosedApps.length; i++) {
        if (ChoosedApps[i].id === ObjectData.id) {
          currentIndex = i;
          break;
        }
      }

      if (currentIndex === -1 || accumulatedTime >= totalDuration) {
        return additionalAppIds;
      }

      let nextIndex = currentIndex + 1;
      while (
        nextIndex < ChoosedApps.length &&
        accumulatedTime < totalDuration
      ) {
        const nextApp = ChoosedApps[nextIndex];
        const prevApp = ChoosedApps[nextIndex - 1];

        if (prevApp.timeEnd !== nextApp.timeStart) {
          break;
        }

        if (nextApp.isTaken === 1) {
          break;
        }

        additionalAppIds.push(nextApp.id);
        accumulatedTime += nextApp.timeLength;
        nextIndex++;
      }

      return additionalAppIds;
    };

    const additionalAppIds = findAdditionalAppointments();

    const data2 = {
      Appointment: {
        Id: ObjectData.id,
        PhoneNumber: User.phoneNumber,
        Name: User.name,
        IsTaken: 1,
        Date: ObjectData.date,
        TimeStart: ObjectData.timeStart,
        TimeEnd: actualEndTime,
        TimeLength: totalDuration,
        BarberPhoneNumber: ObjectData.barberPhoneNumber,
      },
      Barber: {
        ExpoToken: AllData.barberData.expoToken,
      },
      Lang: Lang,
      BookedServices: SelectedServices,
      AllServices: SelectedServices,
      AdditionalAppointmentIds: additionalAppIds,
    };

    try {
      const response = await axios.put(
        `${MyDomain}Appointments2/UpdateAppWithServices`,
        data2,
        {
          timeout: 10000,
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      setValue(1);
      setStep(5);
      setSelectedServices([]);

      setTimeout(() => {
        setOpenDrawer(false);
      }, 100);
    } catch (error) {
      console.error("❌ Error booking appointment:", error);

      if (error.code === "ECONNABORTED") {
        alert("Request timeout. Please check your internet connection.");
      } else if (error.response) {
        alert(`Booking failed: someone already booked this app`);
      } else if (error.request) {
        alert("Network error. Please check your internet connection.");
      } else {
        alert("Failed to book appointment. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const totalPrice = SelectedServices.reduce(
    (sum, service) => sum + parseFloat(service.Price || 0),
    0,
  );

  useEffect(() => {
    if (!BarberPhoneNumber && !ObjectData?.barberPhoneNumber) {
      console.warn("⚠️ No barber phone number available");
      return;
    }

    const barberPhone = ObjectData?.barberPhoneNumber || BarberPhoneNumber;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${MyDomain}appointmentHub`)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return Math.random() * 10000;
          }
          return null;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connection.onclose((error) => {
      console.log("🔌 Modal SignalR connection closed");
      if (error) console.log("Reason:", error.message);
    });

    connection.onreconnecting(() => {
      console.log("🔄 Modal SignalR reconnecting...");
    });

    connection.onreconnected((connectionId) => {
      console.log("✅ Modal SignalR reconnected! ID:", connectionId);
    });

    const startConnection = async () => {
      try {
        if (connection.state === signalR.HubConnectionState.Disconnected) {
          await connection.start();

          connection.on("ReceiveBookingUpdate", (data) => {
            setOpenDrawer(false);
          });

          connection.on("AppointmentCreated", (data) => {
            setOpenDrawer(false);
          });
        }
      } catch (err) {
        console.error("❌ Modal SignalR connection error:", err);
        setTimeout(() => {
          if (connection.state === signalR.HubConnectionState.Disconnected) {
            startConnection();
          }
        }, 5000);
      }
    };

    startConnection();

    return () => {
      if (connection) {
        connection.off("ReceiveBookingUpdate");
        connection.off("AppointmentCreated");

        const state = connection.state;
        if (
          state === signalR.HubConnectionState.Connected ||
          state === signalR.HubConnectionState.Connecting
        ) {
          connection
            .stop()
            .then(() => console.log("✅ Modal SignalR disconnected"))
            .catch((err) => {
              if (!err.message?.includes("connection being closed")) {
                console.log("⚠️ Error stopping Modal SignalR:", err.message);
              }
            });
        }
      }
    };
  }, [BarberPhoneNumber, ObjectData?.barberPhoneNumber, setOpenDrawer]);

  return (
    <Root>
      <CssBaseline />
      <Global
        styles={{
          "@import":
            "url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap')",
          ".MuiDrawer-root > .MuiPaper-root": {
            height: "auto",
            maxHeight: "90%",
            overflow: "visible",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.08)",
          },
          "*": {
            WebkitTapHighlightColor: "transparent",
            WebkitTouchCallout: "none",
            fontFamily: "'Inter', -apple-system, sans-serif",
          },
        }}
      />
      <SwipeableDrawer
        container={container}
        anchor="bottom"
        open={OpenDrawer}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={0}
        disableSwipeToOpen={true}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          zIndex: 1050,
        }}
      >
        <Puller />
        <StyledBox
          sx={{
            p: 3,
            pt: 4,
            direction: isRTL ? "rtl" : "ltr",
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: "24px", textAlign: "center" }}>
            <h2
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: "#0f172a",
                margin: "0 0 6px 0",
                letterSpacing: "-0.3px",
              }}
            >
              {t("ConfirmYourBooking")}
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#64748b",
                margin: 0,
              }}
            >
              {t("ReviewAppointmentDetails")}
            </p>
          </div>

          {/* Appointment Info */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                textAlign: "center",
                padding: "14px 8px",
                background: "#f8fafc",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "20px",
                  marginBottom: "6px",
                }}
              >
                📅
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  marginBottom: "4px",
                  fontWeight: "500",
                }}
              >
                {t("Date")}
              </div>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#0f172a",
                }}
              >
                {ObjectData.date}
              </div>
            </div>

            <div
              style={{
                textAlign: "center",
                padding: "14px 8px",
                background: "#f8fafc",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "20px",
                  marginBottom: "6px",
                }}
              >
                🕐
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  marginBottom: "4px",
                  fontWeight: "500",
                }}
              >
                {t("Time")}
              </div>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#0f172a",
                }}
              >
                {ObjectData.timeStart}
              </div>
            </div>

            <div
              style={{
                textAlign: "center",
                padding: "14px 8px",
                background: "#f8fafc",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: "20px",
                  marginBottom: "6px",
                }}
              >
                ⏱️
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  marginBottom: "4px",
                  fontWeight: "500",
                }}
              >
                {t("Duration")}
              </div>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#0f172a",
                }}
              >
                {totalDuration} {t("Min")}
              </div>
            </div>
          </div>

          {/* Services */}
          {SelectedServices.length > 0 && (
            <div
              style={{
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#64748b",
                  marginBottom: "10px",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {t("SelectedServicesUpper")}
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {SelectedServices.map((service, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 0",
                      borderBottom:
                        index < SelectedServices.length - 1
                          ? "1px solid #e2e8f0"
                          : "none",
                      direction: isRTL ? "rtl" : "ltr",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#0f172a",
                          marginBottom: "2px",
                        }}
                      >
                        {service.Service}
                      </div>
                      {service.Duration && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          {service.Duration} {t("Min")}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        color: "#0f172a",
                        marginLeft: isRTL ? 0 : "16px",
                        marginRight: isRTL ? "16px" : 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {service.Price} {currencySymbol}
                    </div>
                  </div>
                ))}

                {/* Total */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: "12px",
                    marginTop: "8px",
                    borderTop: "2px solid #e2e8f0",
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "700",
                      color: "#0f172a",
                    }}
                  >
                    {t("TotalPrice")}
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#10b981",
                    }}
                  >
                    {totalPrice.toFixed(2)} {currencySymbol}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <button
              onClick={toggleDrawer(false)}
              disabled={IsLoading}
              style={{
                flex: 1,
                padding: "14px",
                fontSize: "15px",
                fontWeight: "600",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                color: "#64748b",
                cursor: IsLoading ? "not-allowed" : "pointer",
                opacity: IsLoading ? 0.5 : 1,
                transition: "all 0.2s",
              }}
            >
              {t("Cancel")}
            </button>
            <button
              onClick={BookApp}
              disabled={IsLoading}
              style={{
                flex: 2,
                padding: "14px",
                fontSize: "15px",
                fontWeight: "600",
                border: "none",
                borderRadius: "12px",
                backgroundColor: IsLoading ? "#94a3b8" : "#10b981",
                color: "#ffffff",
                cursor: IsLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                boxShadow: IsLoading
                  ? "none"
                  : "0 2px 8px rgba(16,185,129,0.2)",
              }}
            >
              {IsLoading ? (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "14px",
                      height: "14px",
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTopColor: "#ffffff",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  {t("BookingInProgress")}
                </span>
              ) : (
                t("ConfirmBooking")
              )}
            </button>
          </div>
        </StyledBox>
      </SwipeableDrawer>

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Root>
  );
}

export default MPHomeModalMakeApp;
